// Fire-and-forget client → server error pipeline. Posts a small JSON payload
// to /api/log/client which Pino-logs it under the `client_error: true` tag.
//
// Goals:
//  - Make production-side failures visible without browser devtools.
//  - Never let logging itself become a foot-gun: every call is wrapped in
//    try/catch, swallows network errors, and uses keepalive so it survives
//    page navigation.
//  - No retries, no queue. If the report drops, the cost is just one missing
//    log line — not worth the complexity here.

type Level = 'error' | 'warn' | 'info'

interface LogPayload {
  level?: Level
  message: string
  url?: string
  status?: number
  code?: string
  request_id?: string
  stack?: string
  context?: Record<string, unknown>
}

const ENDPOINT = '/api/log/client'

// Avoid spamming: if the same message fires repeatedly inside this window,
// suppress duplicates. A failing poll on a backgrounded tab can otherwise
// generate hundreds of identical entries.
const DEDUPE_WINDOW_MS = 5_000
const recentSends = new Map<string, number>()

function shouldSend(key: string): boolean {
  const now = Date.now()
  const last = recentSends.get(key)
  if (last && now - last < DEDUPE_WINDOW_MS) return false
  recentSends.set(key, now)
  // Trim the map opportunistically so it doesn't grow unbounded over a long session.
  if (recentSends.size > 200) {
    const cutoff = now - DEDUPE_WINDOW_MS
    for (const [k, t] of recentSends) if (t < cutoff) recentSends.delete(k)
  }
  return true
}

function send(payload: LogPayload): void {
  try {
    const body = JSON.stringify({
      ...payload,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
    })
    // keepalive: lets the request complete even if the page is unloading.
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {
      // Swallow — we're already in the error path; a failed log post must
      // not bubble up and confuse the caller.
    })
  } catch {
    // JSON.stringify or fetch construction failed — give up silently.
  }
}

export const clientLog = {
  error(payload: LogPayload): void {
    const key = `error:${payload.code ?? payload.message}:${payload.url ?? ''}:${payload.status ?? ''}`
    if (!shouldSend(key)) return
    send({ level: 'error', ...payload })
  },
  warn(payload: LogPayload): void {
    const key = `warn:${payload.code ?? payload.message}:${payload.url ?? ''}`
    if (!shouldSend(key)) return
    send({ level: 'warn', ...payload })
  },
  /**
   * Bypass the dedupe window — every call ships. For diagnostic telemetry
   * (click traces, state-transition pings) where each event is genuinely
   * unique and we want the timeline preserved.
   */
  info(payload: LogPayload): void {
    send({ level: 'info', ...payload })
  },
}

/**
 * Install global catch-alls for uncaught script errors and unhandled promise
 * rejections. Without this, errors that don't propagate through the api
 * `request` helper (render errors, third-party callbacks, dropped fetches in
 * libraries) leave no server-side trace — which is exactly what bit us on the
 * Users tab "generic error" with no server-side correlate.
 *
 * Idempotent: safe to call multiple times. Returns the listener references in
 * case a caller wants to detach them (mostly useful for tests).
 */
let installed = false
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (ev) => {
    const err = ev.error
    clientLog.error({
      message: ev.message || (err instanceof Error ? err.message : 'window_error'),
      code: 'window_error',
      stack: err instanceof Error ? err.stack : undefined,
      context: {
        filename: ev.filename || undefined,
        lineno: ev.lineno || undefined,
        colno: ev.colno || undefined,
      },
    })
  })

  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason
    clientLog.error({
      message:
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'unhandled_rejection',
      code: 'unhandled_rejection',
      stack: reason instanceof Error ? reason.stack : undefined,
      context: {
        reason_type: typeof reason,
      },
    })
  })
}
